import { itemMediaKey, unitMediaKey } from '@/api/masters';
import type { Permission } from '@/api/generated/enums';

/**
 * Which gallery is being shown.
 *
 * <p>
 * <strong>The two levels differ in four values and nothing else</strong> — the route, the cache key,
 * the permission, and what the copy calls the thing. Everything a gallery actually does (promote to
 * main, remove, open the viewer) is identical, and two components would be two places for it to
 * drift.
 * </p>
 */
export type MediaScope =
  { level: 'item'; itemId: number; name: string } | { level: 'unit'; unitId: number; name: string };

/** The route, cache key and permission a scope implies. */
export function galleryOf(scope: MediaScope): {
  basePath: string;
  cacheKey: readonly string[];
  permission: Permission;
} {
  return scope.level === 'unit'
    ? {
        basePath: `/api/item-unit/${scope.unitId}/media`,
        cacheKey: unitMediaKey,
        // Photographing the rail is wardrobe work and editing the catalogue is not, so the two
        // levels ask for different permissions — the default catalogue gives wardrobe staff one
        // without the other.
        permission: 'ItemUnit:Edit',
      }
    : {
        basePath: `/api/item/${scope.itemId}/media`,
        cacheKey: itemMediaKey,
        permission: 'Item:Edit',
      };
}

/** Bytes as a shop reads them. */
export function formatSize(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }

  if (bytes < 1024 * 1024) {
    return `${Math.round(bytes / 1024)} KB`;
  }

  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
